import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  capitalize,
  useTheme,
} from '@mui/material';
import { mergeWorkoutLayers } from '@strength/domain-workout';
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdInfoOutline,
} from '@teamworksdev/icons';
import {
  FormEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isTRPCClientError } from '../../../../trpc/common';
import { trpc } from '../../../../trpc/react';
import { FormNotification } from '../../../components/form/form-notification';
import { InputLabel } from '../../../components/input/input-label';
import { toast } from '../../../hooks/useToast';
import { BlockPreview, BlockPublishedWorkoutLayer } from '../types';

export const DISPLAY_TAB_FORM_ID = 'display-tab-form';
// A second, fieldless form: the shared footer's "Stop Displaying" button
// (rendered outside this component, in ExportDrawer.tsx) targets this by
// id so clearSelection - which needs this tab's captured week/day - can
// stay wired here rather than lifting the whole handler up.
export const DISPLAY_STOP_FORM_ID = 'display-tab-stop-form';

const MAX_SECTIONS_WITHOUT_PAGE_DURATION = 6;
const HEADER_TEXT_MAX_LENGTH = 100;

export type PageDurationUnit = 'seconds' | 'minutes' | 'hours';

/**
 * Converts a coach-entered duration + unit to seconds for the save payload.
 * The server rejects a duration at or below 6 sections, so this omits it
 * (returns null) even if a value is still sitting in state from before the
 * coach deselected back down to 6.
 */
export function resolvePageDurationSeconds(
  sectionCount: number,
  pageDurationValue: number | null,
  pageDurationUnit: PageDurationUnit,
): number | null {
  if (sectionCount <= MAX_SECTIONS_WITHOUT_PAGE_DURATION) return null;
  if (pageDurationValue == null) return null;

  const secondsPerUnit: Record<PageDurationUnit, number> = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
  };
  return pageDurationValue * secondsPerUnit[pageDurationUnit];
}

type Baseline = {
  cycleId: number | undefined;
  sessionId: number | undefined;
  sectionIds: string;
  quote: string | null;
  pageDurationValue: number | null;
  pageDurationUnit: PageDurationUnit;
  highlightSupersets: boolean;
};

interface DisplayTabProps {
  block: BlockPreview;
  blockPublishedWorkoutLayers: BlockPublishedWorkoutLayer[];
  initCycleId: number | undefined;
  initSessionId: number | undefined;
  onOpenChange: (open: boolean) => void;
  onDisplayStopped: () => void;
  onDisabledChange: (disabled: boolean) => void;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
  onActiveSelectionChange: (hasActiveSelection: boolean) => void;
}

const sortedSectionKey = (sectionIds: string[]) =>
  [...sectionIds].sort().join(',');

export const DisplayTab = ({
  block,
  blockPublishedWorkoutLayers,
  initCycleId,
  initSessionId,
  onOpenChange,
  onDisplayStopped,
  onDisabledChange,
  onUnsavedChangesChange,
  onActiveSelectionChange,
}: DisplayTabProps) => {
  const { t } = useTranslation();
  const {
    palette: { info },
  } = useTheme();
  const utils = trpc.useUtils();
  const selectionQuery = trpc.display.getSelection.useQuery({
    teamId: block.teamId,
  });
  const saveMutation = trpc.display.saveSelection.useMutation();
  const clearSelectionMutation = trpc.display.clearSelection.useMutation();
  const [saveError, setSaveError] = useState<string | null>(null);

  const [cycleId, setCycleId] = useState<number | undefined>(initCycleId);
  const [sessionId, setSessionId] = useState<number | undefined>(initSessionId);
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [quote, setQuote] = useState<string | null>(null);
  const [pageDurationValue, setPageDurationValue] = useState<number | null>(
    null,
  );
  const [pageDurationUnit, setPageDurationUnit] =
    useState<PageDurationUnit>('seconds');
  const [highlightSupersets, setHighlightSupersets] = useState(true);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const durationInputRef = useRef<HTMLInputElement>(null);
  // Snapshot of the seeded values, captured once getSelection resolves, so
  // the unsaved-changes check (below) compares against what was actually
  // loaded rather than re-deriving a "default" that shifts as the coach
  // changes week/day. State rather than a ref because a successful save
  // re-baselines, and that has to re-run the isDirty comparison.
  const [baseline, setBaseline] = useState<Baseline | null>(null);
  const hasSeededRef = useRef(false);

  // Displays resolve the current published layer only (same source
  // saveSelection validates against). Ignore non-current versions and any
  // draft-only workouts.
  const currentPublishedLayers = useMemo(
    () => blockPublishedWorkoutLayers.filter((l) => l.isCurrent),
    [blockPublishedWorkoutLayers],
  );

  // TeamDisplaySelection holds one row per TEAM, so the team's active
  // selection may belong to a different block than the one this drawer was
  // opened from. Only treat it as this block's when its workout is actually
  // a current published layer here: otherwise we'd seed another block's
  // sectionIds into this block's picker, and offer "Stop Displaying" on a
  // block whose header shows no badge. DisplayedWorkoutBadge scopes itself
  // the same way.
  const selectionForThisBlock = useMemo(() => {
    const selection = selectionQuery.data;
    if (!selection) return null;
    return currentPublishedLayers.some(
      (l) => l.workoutId === selection.workoutId,
    )
      ? selection
      : null;
  }, [selectionQuery.data, currentPublishedLayers]);

  const sectionsForWorkout = useCallback(
    (
      targetCycleId: number | undefined,
      targetSessionId: number | undefined,
    ) => {
      const layer = currentPublishedLayers.find(
        (l) =>
          l.cycleSortOrder === targetCycleId &&
          l.sessionSortOrder === targetSessionId,
      );
      if (!layer) return [];
      return mergeWorkoutLayers(layer).sections.map((s) => s.workoutSectionId);
    },
    [currentPublishedLayers],
  );

  useLayoutEffect(() => {
    if (
      hasSeededRef.current ||
      selectionQuery.isLoading ||
      currentPublishedLayers.length === 0
    )
      return;
    hasSeededRef.current = true;

    const selection = selectionForThisBlock;
    const seededLayer = selection
      ? currentPublishedLayers.find((l) => l.workoutId === selection.workoutId)
      : undefined;

    const seededCycleId = seededLayer?.cycleSortOrder ?? initCycleId;
    const seededSessionId = seededLayer?.sessionSortOrder ?? initSessionId;
    const seededSectionIds =
      selection?.sectionIds ??
      sectionsForWorkout(seededCycleId, seededSessionId);
    const seededQuote = selection?.quote ?? null;
    const seededDuration = selection?.pageDurationSeconds ?? null;
    const seededHighlight = selection?.highlightSupersets ?? true;

    setCycleId(seededCycleId);
    setSessionId(seededSessionId);
    setSectionIds(seededSectionIds);
    setQuote(seededQuote);
    setPageDurationValue(seededDuration);
    setPageDurationUnit('seconds');
    setHighlightSupersets(seededHighlight);

    setBaseline({
      cycleId: seededCycleId,
      sessionId: seededSessionId,
      sectionIds: sortedSectionKey(seededSectionIds),
      quote: seededQuote,
      pageDurationValue: seededDuration,
      pageDurationUnit: 'seconds',
      highlightSupersets: seededHighlight,
    });
  }, [
    selectionQuery.isLoading,
    selectionForThisBlock,
    currentPublishedLayers,
    initCycleId,
    initSessionId,
    sectionsForWorkout,
  ]);

  useLayoutEffect(() => {
    onActiveSelectionChange(!!selectionForThisBlock);
  }, [selectionForThisBlock, onActiveSelectionChange]);

  const publishedLayer = useMemo(
    () =>
      currentPublishedLayers.find(
        (l) => l.cycleSortOrder === cycleId && l.sessionSortOrder === sessionId,
      ),
    [currentPublishedLayers, cycleId, sessionId],
  );

  const sectionOptions = useMemo(() => {
    if (!publishedLayer) return [];
    return mergeWorkoutLayers(publishedLayer).sections.map((s) => ({
      id: s.workoutSectionId,
      name: s.name,
    }));
  }, [publishedLayer]);

  const isAllSectionsSelected =
    sectionOptions.length > 0 && sectionIds.length === sectionOptions.length;

  const handleSelectAllSectionsChange = (checked: boolean) => {
    setSectionIds(checked ? sectionOptions.map((s) => s.id) : []);
  };

  const handleSectionChange = (sectionId: string, checked: boolean) => {
    setSectionIds((prev) =>
      checked ? [...prev, sectionId] : prev.filter((id) => id !== sectionId),
    );
  };

  const handleCycleChange = (value: number) => {
    setCycleId(value);
    setSectionIds(sectionsForWorkout(value, sessionId));
  };

  const handleSessionChange = (value: number) => {
    setSessionId(value);
    setSectionIds(sectionsForWorkout(cycleId, value));
  };

  const showPageDuration =
    sectionIds.length > MAX_SECTIONS_WITHOUT_PAGE_DURATION;
  const durationError =
    submitAttempted && showPageDuration && pageDurationValue == null;

  const disabled =
    !cycleId ||
    !sessionId ||
    !publishedLayer ||
    sectionIds.length === 0 ||
    saveMutation.isLoading;

  useLayoutEffect(() => {
    onDisabledChange(disabled);
  }, [disabled, onDisabledChange]);

  const isDirty = useMemo(() => {
    if (!baseline) return false;
    return (
      cycleId !== baseline.cycleId ||
      sessionId !== baseline.sessionId ||
      sortedSectionKey(sectionIds) !== baseline.sectionIds ||
      quote !== baseline.quote ||
      pageDurationValue !== baseline.pageDurationValue ||
      pageDurationUnit !== baseline.pageDurationUnit ||
      highlightSupersets !== baseline.highlightSupersets
    );
  }, [
    baseline,
    cycleId,
    sessionId,
    sectionIds,
    quote,
    pageDurationValue,
    pageDurationUnit,
    highlightSupersets,
  ]);

  useLayoutEffect(() => {
    onUnsavedChangesChange(isDirty);
  }, [isDirty, onUnsavedChangesChange]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setSaveError(null);
    if (showPageDuration && pageDurationValue == null) {
      durationInputRef.current?.focus();
      return;
    }
    if (!publishedLayer) return;

    // Print trims its own free-text before submitting; match that so a
    // whitespace-only header doesn't reserve header space on the board.
    const trimmedQuote = quote?.trim() ? quote.trim() : null;
    const resolvedDuration = resolvePageDurationSeconds(
      sectionIds.length,
      pageDurationValue,
      pageDurationUnit,
    );

    saveMutation.mutate(
      {
        teamId: block.teamId,
        blockId: block.id,
        workoutId: publishedLayer.workoutId,
        sectionIds,
        quote: trimmedQuote,
        pageDurationSeconds: resolvedDuration,
        highlightSupersets,
      },
      {
        onSuccess: () => {
          // Re-baseline against what was actually persisted. The seed effect
          // runs once, so without this the tab stays permanently "dirty"
          // after a save and the two-step tab-switch banner fires even
          // though there is nothing unsaved.
          setQuote(trimmedQuote);
          setBaseline({
            cycleId,
            sessionId,
            sectionIds: sortedSectionKey(sectionIds),
            quote: trimmedQuote,
            pageDurationValue,
            pageDurationUnit,
            highlightSupersets,
          });
          utils.display.getSelection.invalidate({ teamId: block.teamId });
          toast({
            severity: 'success',
            title: t('success'),
            description: t('digitalDisplaySaved', {
              cycleLabel: capitalize(block.cycleLabel),
              cycleNumber: cycleId,
              sessionLabel: capitalize(block.sessionLabel),
              sessionNumber: sessionId,
            }),
          });
          onOpenChange(false);
        },
        onError: (error) => {
          if (
            isTRPCClientError(error) &&
            (error.data?.code === 'BAD_REQUEST' ||
              error.data?.code === 'NOT_FOUND')
          ) {
            setSaveError(error.message);
          } else {
            toast({ severity: 'error', title: t('somethingWentWrong') });
          }
        },
      },
    );
  };

  // Bound to DISPLAY_STOP_FORM_ID - the shared footer's "Stop Displaying"
  // button (in ExportDrawer.tsx) submits this by id.
  const handleStopSubmit = (e: FormEvent) => {
    e.preventDefault();
    const displayedLayer = currentPublishedLayers.find(
      (l) => l.workoutId === selectionForThisBlock?.workoutId,
    );
    const stoppedCycleId = displayedLayer?.cycleSortOrder ?? cycleId;
    const stoppedSessionId = displayedLayer?.sessionSortOrder ?? sessionId;

    clearSelectionMutation.mutate(
      { teamId: block.teamId },
      {
        onSuccess: () => {
          utils.display.getSelection.invalidate({ teamId: block.teamId });
          toast({
            severity: 'success',
            title: t('success'),
            description: t('digitalDisplayStopped', {
              cycleLabel: capitalize(block.cycleLabel),
              cycleNumber: stoppedCycleId,
              sessionLabel: capitalize(block.sessionLabel),
              sessionNumber: stoppedSessionId,
            }),
          });
          onOpenChange(false);
          // Deferred: the modal restores focus as it unmounts its trap, so
          // reclaiming focus has to happen after that, not during this tick.
          requestAnimationFrame(onDisplayStopped);
        },
        onError: () => {
          toast({ severity: 'error', title: t('somethingWentWrong') });
        },
      },
    );
  };

  if (selectionQuery.isLoading) {
    return (
      <div className="flex grow items-center justify-center p-8">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <form
        id={DISPLAY_TAB_FORM_ID}
        onSubmit={handleSubmit}
        className="flex flex-col p-1 px-8 grow gap-4 pb-6"
      >
        {saveError && (
          <FormNotification
            title={t('somethingWentWrong')}
            type="warning"
            onDismiss={() => setSaveError(null)}
          >
            {saveError}
          </FormNotification>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
            <TextField
              select
              label={capitalize(block.cycleLabel)}
              value={cycleId ?? ''}
              onChange={(e) => handleCycleChange(Number(e.target.value))}
            >
              {block.sortedCycles.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
            <TextField
              select
              label={capitalize(block.sessionLabel)}
              value={sessionId ?? ''}
              onChange={(e) => handleSessionChange(Number(e.target.value))}
            >
              {block.sortedSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box className="flex">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={highlightSupersets}
                onChange={(e) => setHighlightSupersets(e.target.checked)}
              />
            }
            label={t('highlightSupersets')}
            slotProps={{ typography: { variant: 'body2' } }}
            sx={{ '& .MuiFormControlLabel-label': { ml: 1 } }}
          />
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <div className="flex items-center gap-1">
            <InputLabel label={t('sections')} />
            <Tooltip title={t('sectionsInfoTooltip')} arrow describeChild>
              <IconButton
                size="small"
                aria-label={t('sectionsInfoTooltipLabel')}
                sx={{ p: 0 }}
              >
                <MdInfoOutline size={14} />
              </IconButton>
            </Tooltip>
          </div>
          <Box
            role="group"
            aria-label={t('sections')}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  icon={<MdCheckBoxOutlineBlank size={20} />}
                  checkedIcon={<MdCheckBox size={20} color={info.main} />}
                  checked={isAllSectionsSelected}
                  onChange={(e) =>
                    handleSelectAllSectionsChange(e.target.checked)
                  }
                />
              }
              label={isAllSectionsSelected ? t('deselectAll') : t('selectAll')}
              sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold' } }}
            />
            <Divider className="my-1" />
            {sectionOptions.map((section) => (
              <FormControlLabel
                key={section.id}
                control={
                  <Checkbox
                    icon={<MdCheckBoxOutlineBlank size={20} />}
                    checkedIcon={<MdCheckBox size={20} color={info.main} />}
                    checked={sectionIds.includes(section.id)}
                    onChange={(e) =>
                      handleSectionChange(section.id, e.target.checked)
                    }
                  />
                }
                label={section.name}
              />
            ))}
          </Box>
        </Box>

        <div className="flex flex-col">
          <TextField
            label={t('headerText')}
            multiline
            maxRows={5}
            minRows={3}
            value={quote ?? ''}
            inputProps={{ maxLength: HEADER_TEXT_MAX_LENGTH }}
            onChange={(e) => {
              const value = e.target.value;
              setQuote(value.length > 0 ? value : null);
            }}
          />
          <FormHelperText
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mx: 0,
              mt: 1,
            }}
          >
            <span>{t('headerTextHelperText')}</span>
            <span>
              {quote?.length ?? 0}/{HEADER_TEXT_MAX_LENGTH}
            </span>
          </FormHelperText>
        </div>

        {showPageDuration && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              inputRef={durationInputRef}
              type="number"
              label={t('pageDuration')}
              value={pageDurationValue ?? ''}
              inputProps={{ min: 1 }}
              onChange={(e) => {
                const value = e.target.value;
                setPageDurationValue(value === '' ? null : Number(value));
              }}
              error={durationError}
              helperText={
                durationError ? t('pageDurationRequiredError') : undefined
              }
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label={t('unit')}
              value={pageDurationUnit}
              onChange={(e) =>
                setPageDurationUnit(e.target.value as PageDurationUnit)
              }
              sx={{ width: 140 }}
            >
              <MenuItem value="seconds">{t('seconds')}</MenuItem>
              <MenuItem value="minutes">{t('minutes')}</MenuItem>
              <MenuItem value="hours">{t('hours')}</MenuItem>
            </TextField>
          </Box>
        )}
      </form>
      <form
        id={DISPLAY_STOP_FORM_ID}
        onSubmit={handleStopSubmit}
        className="hidden"
        aria-hidden="true"
      />
    </>
  );
};
